import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal = ({ isOpen, onClose }: TermsModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display font-bold">Terms of Service</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm font-body">
            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Acceptance of Terms</h3>
              <p className="text-muted-foreground leading-[1.65]">
                By accessing and using Sink Bike Run, you accept and agree to be bound by the 
                terms and provision of this agreement. If you do not agree to abide by the above, 
                please do not use this service.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Use License</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Permission is granted to temporarily use Sink Bike Run for personal, 
                non-commercial triathlon preparation purposes. This is the grant of a license, 
                not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display</li>
                <li>Attempt to reverse engineer any software contained on the website</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">User-Generated Content</h3>
              <p className="text-muted-foreground leading-[1.65]">
                You retain ownership of any checklist content you create. By using our service, 
                you grant us a limited license to store and display your content solely for the 
                purpose of providing the service to you.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Use at Your Own Risk</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                You access and use Sink Bike Run entirely at your own risk. We provide no guarantees regarding:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Website availability or uptime</li>
                <li>Accuracy or completeness of information</li>
                <li>Fitness for any particular purpose</li>
                <li>Freedom from errors or interruptions</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Disclaimer of Warranties</h3>
              <p className="text-muted-foreground leading-[1.65]">
                The materials on Sink Bike Run are provided on an 'as is' basis. Sink Bike Run 
                makes no warranties, expressed or implied, and hereby disclaims and negates all 
                other warranties including without limitation, implied warranties or conditions of 
                merchantability, fitness for a particular purpose, or non-infringement of 
                intellectual property or other violation of rights.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Safety Notice</h3>
              <p className="text-muted-foreground leading-[1.65]">
                Our checklists are provided as general guidance for triathlon preparation. 
                Always consult with qualified professionals regarding training, nutrition, and 
                safety protocols. We are not responsible for any injuries or incidents that may 
                occur during training or race participation.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Limitation of Liability</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, Sink Bike Run and its suppliers shall not be liable for any damages of any kind, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Direct, indirect, incidental, or consequential damages</li>
                <li>Loss of data, profits, or business interruption</li>
                <li>Personal injury or property damage</li>
                <li>Any damages resulting from use or inability to use our service</li>
              </ul>
              <p className="text-muted-foreground leading-[1.65] mt-2">
                This limitation applies even if we have been advised of the possibility of such damages.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Revisions and Errata</h3>
              <p className="text-muted-foreground leading-[1.65]">
                The materials appearing on Sink Bike Run could include technical, typographical, 
                or photographic errors. We do not warrant that any of the materials are accurate, 
                complete, or current and may make changes at any time without notice.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Governing Law and Jurisdiction</h3>
              <p className="text-muted-foreground leading-[1.65]">
                These terms and conditions are governed by and construed in accordance with the 
                laws of the State of Minnesota and the United States. You irrevocably submit to 
                the exclusive jurisdiction of the state and federal courts located in Minnesota 
                for the resolution of any disputes arising from these terms or your use of our service.
              </p>
              <p className="text-foreground font-body leading-[1.7] mt-2">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TermsModal;